'use client'

import React from 'react'
import Link from 'next/link'
import BlogPostLayout from '@/app/components/BlogPostLayout'
import { BlogTLDR, BlogKeyTakeaways, BlogFAQ, BlogSources, BlogLastUpdated, BlogComparisonTable, BlogAppCTA } from '@/app/components/blog/BlogAeo'
import RiskQuizInlineNudge from '@/app/components/blog/RiskQuizInlineNudge'
import InlineGuessReveal from '@/app/components/blog/InlineGuessReveal'
import KeyStats from '@/app/components/blog/KeyStats'

export default function PostContent() {
  return (
    <BlogPostLayout
      title="Does CeraVe Have Microplastics? All 71 Published Formulas Checked (2026)"
      author="MicroPlastics Research Desk"
      date="August 8, 2026"
      readTime="11 min read"
      heroImage="/blog/heroes/does-cerave-have-microplastics.webp"
      heroImageAlt="Unbranded blue-accent moisturizer, lotion, cleanser and cream grouped beside a bathroom basin"
    >
      <BlogLastUpdated iso="2026-08-08" formatted="August 8, 2026" />

      <BlogTLDR>
        <strong>Four. Out of 71 formulas CeraVe publishes an ingredient list
        for, four contain a solid synthetic polymer particle.</strong> That is
        the cleanest result we have recorded for any large brand in any category
        — cleaner than every makeup brand in our{' '}
        <Link href="/blog/microplastics-in-makeup-brands-audited" className="text-forest underline">July audit</Link>{' '}
        and level with Cetaphil. The four are the{' '}
        <strong>Makeup Removing Cleanser Balm</strong> (polyethylene), the{' '}
        <strong>Soothing Body Wash for Very Dry Skin</strong> (silica dimethyl
        silylate), the <strong>Skin Renewing Retinol Serum</strong> (vinyl
        dimethicone crosspolymer) and the{' '}
        <strong>Ultra-Light Moisturizing Lotion SPF 30</strong> (methyl
        methacrylate crosspolymer). Critically:{' '}
        <strong>the three ceramide moisturisers that most people actually buy
        contain none.</strong> If you use CeraVe Moisturizing Cream, Daily
        Moisturizing Lotion or the Foaming Cleanser, there is nothing here for
        you to change.
      </BlogTLDR>

      <BlogKeyTakeaways
        items={[
          <><strong>4 of 71</strong> published CeraVe formulas contain a solid synthetic polymer particle — about 6%.</>,
          <>The <strong>Moisturizing Cream, Daily Moisturizing Lotion, Hydrating Cleanser and Foaming Cleanser are all clean.</strong></>,
          <>The hits are one balm, one body wash, one serum and one SPF lotion — <strong>none of them core products</strong>.</>,
          <>CeraVe publishes <strong>complete INCI lists</strong> on its own product pages, which is more than most food and drink brands manage.</>,
          <>Plenty of CeraVe formulas contain <strong>dimethicone</strong>. That is a silicone, not a microplastic — a liquid polymer, not a solid particle.</>,
          <>Nothing found here is banned or non-compliant. Leave-on cosmetic microplastics remain legal in the EU until <strong>October 2035</strong>.</>,
          <>The one to look at if you use it daily is the <strong>SPF 30 lotion</strong> — a leave-on that stays on your skin all day.</>,
        ]}
      />

      <KeyStats
        heading="The CeraVe audit"
        stats={[
          { value: '71', label: 'formulas with published INCI', context: 'pulled from cerave.com product pages in August 2026', source: 'MicroPlastics Research Desk audit' },
          { value: '4', label: 'contain a solid polymer particle', context: 'roughly 6% — the lowest rate of any large brand we have audited', source: 'MicroPlastics Research Desk audit' },
          { value: '0', label: 'of the three core ceramide moisturisers', context: 'Moisturizing Cream, Daily Moisturizing Lotion and Facial Moisturising Lotion are all clean', source: 'cerave.com ingredient listings', sourceUrl: 'https://www.cerave.com/' },
          { value: '2035', label: 'when the EU restriction bites for leave-on cosmetics', context: 'October 2035 under REACH Annex XVII entry 78', source: 'Commission Regulation (EU) 2023/2055', sourceUrl: 'https://eur-lex.europa.eu/eli/reg/2023/2055/oj' },
        ]}
      />

      <h2>The answer people are actually looking for</h2>
      <p>
        Most people searching this are holding a tub of CeraVe Moisturizing Cream
        and want to know whether to throw it out.{' '}
        <strong>You should not. It is clean.</strong> So is the Daily
        Moisturizing Lotion, so is the Hydrating Cleanser, and so is the Foaming
        Cleanser.
      </p>
      <p>
        We are putting that first because the honest finding here is
        reassurance, and burying reassurance under 800 words of context to keep
        you reading would be the wrong way to write this page. CeraVe came out
        of this audit better than any brand we have run the method against.
      </p>

      <InlineGuessReveal
        source="cerave_clean_count"
        question="Of the 71 CeraVe formulas that publish an ingredient list, how many contain a solid microplastic particle?"
        options={[
          { label: 'Around 30' },
          { label: 'About 15' },
          { label: 'Four', correct: true },
          { label: 'None at all' },
        ]}
        answer="four"
        answerNote={
          <>
            A cleansing balm, a body wash, the retinol serum and one SPF lotion.
            Every core moisturiser and cleanser in the range came back clean.
            For comparison, the prestige makeup brands we audited in July ran
            close to 30%.
          </>
        }
        ctaText="CeraVe is clean. The rest of your shelf might not be — scan it and see."
        ctaLabel="Scan a product"
      />

      <RiskQuizInlineNudge source="blog_inline_cerave" />

      <h2>The four, and what each polymer is doing</h2>

      <BlogComparisonTable
        caption="Every CeraVe formula containing a solid synthetic polymer particle, from CeraVe's own published ingredient lists (checked August 2026)"
        columns={[
          { key: 'product', label: 'Product' },
          { key: 'polymer', label: 'Polymer' },
          { key: 'job', label: 'What it does' },
          { key: 'type', label: 'Rinse-off or leave-on' },
        ]}
        rows={[
          { product: 'Ultra-Light Moisturizing Lotion SPF 30', polymer: 'Methyl methacrylate crosspolymer', job: 'Soft-focus powder that kills the greasy sunscreen finish. This is what makes it feel "ultra-light".', type: 'Leave-on — the one that matters most' },
          { product: 'Skin Renewing Retinol Serum', polymer: 'Vinyl dimethicone crosspolymer', job: 'Elastomer that thickens the serum and gives it slip', type: 'Leave-on' },
          { product: 'Makeup Removing Cleanser Balm', polymer: 'Polyethylene', job: 'Structures the balm so it stays solid in the jar and melts on contact', type: 'Rinse-off' },
          { product: 'Soothing Body Wash for Very Dry Skin', polymer: 'Silica dimethyl silylate', job: 'Silica treated with a silicone coating; suspension and texture', type: 'Rinse-off' },
        ]}
      />

      <p>
        The pattern is worth naming, because it repeats across every brand we
        audit: <strong>solid polymer particles show up wherever a product has to
        feel like something.</strong> A basic moisturiser has to hydrate, and
        oils and ceramides do that. A sunscreen has to hydrate <em>and</em> not
        feel like sunscreen, and that second job is where the polymer spheres
        come in.
      </p>

      <h2>Why the SPF lotion is the one to think about</h2>
      <p>
        Of the four, the Ultra-Light Moisturizing Lotion SPF 30 is the only one
        that is both a <strong>leave-on</strong> and a{' '}
        <strong>plausible daily habit</strong>. A cleansing balm is on your face
        for a minute. A body wash is on for less. The retinol serum is a
        leave-on but most people use it two or three nights a week.
      </p>
      <p>
        Daily facial SPF is the product dermatologists push hardest, so if you
        have taken that advice you are applying methyl methacrylate crosspolymer
        to your face every morning. We want to be measured here:{' '}
        <strong>there is no evidence that this specific exposure causes harm</strong>,
        and skin is a genuinely effective barrier — see our review of{' '}
        <Link href="/blog/microplastics-and-skin-absorption" className="text-forest underline">what the research says about skin absorption</Link>.
        The stronger argument against it is environmental: it washes off at the
        end of the day and goes into the water system.
      </p>
      <p>
        <strong>Do not stop wearing sunscreen over this.</strong> The
        skin-cancer case for daily SPF is overwhelming and the microplastic case
        against this particular ingredient is weak. If you want both, mineral
        sunscreens generally skip the soft-focus polymers — we cover the options
        in{' '}
        <Link href="/blog/best-sunscreens-without-microplastics-brands-ranked" className="text-forest underline">sunscreens without microplastics</Link>.
      </p>

      <h2>The dimethicone question</h2>
      <p>
        If you run a CeraVe ingredient list through an app that flags
        &ldquo;plastics,&rdquo; you will get hits on almost every product,
        because most CeraVe formulas contain{' '}
        <strong>dimethicone</strong>. It is one of the reasons the range feels
        the way it does.
      </p>
      <p>
        Dimethicone is a silicone. It is a synthetic polymer, it is
        petrochemical-derived, and it is <strong>not a microplastic</strong>,
        because it is a liquid rather than a discrete solid particle. The EU
        restriction on synthetic polymer microparticles turns specifically on
        solid particles, and dimethicone falls outside it.
      </p>
      <p>
        You can hold a separate view about silicones — biodegradability is a fair
        debate. But counting them as microplastics is how you end up with a scary
        number that means nothing and cannot be compared with anyone else&apos;s.
        We keep the two categories apart on purpose.
      </p>

      <BlogAppCTA
        headline="Check the rest of your shelf"
        sub="CeraVe came out clean. Scan your sunscreen, serum and cleanser with MicroPlastics to see whether the rest of your routine did too."
      />

      <h2>What CeraVe gets right</h2>
      <p>
        One thing deserves credit and rarely gets it:{' '}
        <strong>CeraVe publishes full INCI lists on its own product pages.</strong>{' '}
        Every formula in this audit came from cerave.com, not from a third-party
        database. That is why we can tell you a number at all.
      </p>
      <p>
        Compare that with the protein aisle, where{' '}
        <Link href="/blog/does-muscle-milk-have-microplastics" className="text-forest underline">Muscle Milk publishes neither an ingredient list nor a packaging material</Link>,
        or with cookware brands that will not name a polymer. A brand that
        publishes everything can be audited and occasionally criticised. A brand
        that publishes nothing cannot be checked at all. The first is better,
        even when the audit finds something.
      </p>

      <h2>What to actually do</h2>
      <ul>
        <li>
          <strong>Keep your Moisturizing Cream.</strong> It is clean. So is the
          Daily Moisturizing Lotion and both main cleansers.
        </li>
        <li>
          <strong>If you want zero polymer particles in a daily SPF</strong>,
          swap the Ultra-Light SPF 30 for a mineral sunscreen. It is the only
          leave-on daily product on the list.
        </li>
        <li>
          <strong>The cleansing balm and body wash are low priority.</strong>{' '}
          Rinse-off, brief contact. Worth knowing, not worth binning.
        </li>
        <li>
          <strong>Ignore dimethicone in this context.</strong> Not a
          microplastic. Different conversation.
        </li>
        <li>
          <strong>Check your other brands.</strong> CeraVe is the good news
          story here. The rest of your shelf may not be — see the{' '}
          <Link href="/blog/drugstore-skincare-microplastics-audited" className="text-forest underline">full three-brand audit</Link>.
        </li>
      </ul>

      <p>
        Read the{' '}
        <Link href="/blog/drugstore-skincare-microplastics-audited" className="text-forest underline">290-formula drugstore audit</Link>,
        the level-pegging{' '}
        <Link href="/blog/does-cetaphil-have-microplastics" className="text-forest underline">Cetaphil result</Link>,
        or the{' '}
        <Link href="/blog/microplastics-in-makeup-brands-audited" className="text-forest underline">makeup brands that scored far worse</Link>.
        To check any product yourself,{' '}
        <Link href="/blog/how-to-check-cosmetic-ingredients-for-microplastics-2026" className="text-forest underline">how to read a cosmetic ingredient list</Link>, and the third brand in the cluster, <Link href="/blog/does-neutrogena-have-microplastics" className="text-forest underline">Neutrogena</Link>.
      </p>

      <p>
        A second dataset: the{' '}
        <Link href="/brands/cerave" className="text-forest underline">
          CeraVe brand page
        </Link>{' '}
        shows what Open Beauty Facts lists on CeraVe ingredient labels, a
        crowdsourced and Europe-weighted sample.
      </p>

      <BlogFAQ
        items={[
          {
            question: 'Does CeraVe have microplastics?',
            answer: 'Four of the 71 CeraVe formulas that publish an ingredient list contain a solid synthetic polymer particle: the Ultra-Light Moisturizing Lotion SPF 30 (methyl methacrylate crosspolymer), the Skin Renewing Retinol Serum (vinyl dimethicone crosspolymer), the Makeup Removing Cleanser Balm (polyethylene) and the Soothing Body Wash for Very Dry Skin (silica dimethyl silylate). That is about 6%, the lowest rate of any large brand we have audited.',
          },
          {
            question: 'Is CeraVe Moisturizing Cream free of microplastics?',
            answer: 'Yes. CeraVe Moisturizing Cream contains no solid synthetic polymer particles according to the ingredient list CeraVe publishes on its own site. The same is true of the Daily Moisturizing Lotion, the Hydrating Cleanser and the Foaming Facial Cleanser. It does contain dimethicone, which is a silicone rather than a microplastic.',
          },
          {
            question: 'Is dimethicone in CeraVe a microplastic?',
            answer: 'No. Dimethicone is a liquid silicone polymer, not a solid particle, so it sits outside the EU definition of synthetic polymer microparticles that the microplastic restriction targets. Apps and ingredient checkers that flag it as plastic are using a broader definition of polymer than the regulation does. There are legitimate separate debates about silicone biodegradability, but it is not a microplastic.',
          },
          {
            question: 'Should I stop using CeraVe sunscreen because of microplastics?',
            answer: 'No. The Ultra-Light Moisturizing Lotion SPF 30 contains methyl methacrylate crosspolymer, which gives it the non-greasy finish, and it is the only daily leave-on in the CeraVe range with a solid polymer particle. But the evidence for daily sunscreen preventing skin cancer is overwhelming and the evidence that this ingredient harms you through skin is essentially absent. If you want to avoid it anyway, mineral sunscreens generally skip these soft-focus polymers.',
          },
          {
            question: 'Is CeraVe or Cetaphil cleaner?',
            answer: 'They are effectively level. CeraVe has 4 of 71 published formulas containing a solid polymer particle and Cetaphil has 5 of 79 — about 6% each. They differ in where the hits fall: CeraVe’s are spread across a balm, a body wash, a serum and an SPF lotion, while Cetaphil’s cluster almost entirely in its SPF moisturisers.',
          },
        ]}
      />

      <BlogSources
        items={[
          { authors: 'MicroPlastics Research Desk', year: 2026, title: 'First-party INCI audit of 71 published CeraVe formulas (product sitemap walked, ingredient lists extracted from cerave.com, August 2026)', publisher: 'MicroPlastics' },
          { authors: 'L’Oréal (CeraVe)', year: 2026, title: 'CeraVe product ingredient listings', publisher: 'cerave.com', url: 'https://www.cerave.com/' },
          { authors: 'European Commission', year: 2023, title: 'Commission Regulation (EU) 2023/2055 restricting synthetic polymer microparticles (REACH Annex XVII entry 78; October 2035 deadline for leave-on cosmetics)', publisher: 'Official Journal of the European Union', url: 'https://eur-lex.europa.eu/eli/reg/2023/2055/oj' },
        ]}
      />
    </BlogPostLayout>
  )
}
